import { useState, useEffect, useCallback } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";

export type StaffRole = "staff" | "manager";
export type AccessType = "owner" | "manager" | "staff" | "none";

interface StaffBusiness {
  id: string;
  business_name: string;
  subscription_plan: string | null;
}

interface DashboardPermissions {
  canViewOverview: boolean;
  canViewBookings: boolean;
  canViewPOS: boolean;
  canViewCustomers: boolean;
  canViewPractitioners: boolean;
  canViewServices: boolean;
  canViewInventory: boolean;
  canViewReports: boolean;
  canViewSettings: boolean;
  canViewStaff: boolean;
  canViewSubscription: boolean;
  canViewBusinessProfile: boolean;
}

const NO_PERMISSIONS: DashboardPermissions = {
  canViewOverview: false,
  canViewBookings: false,
  canViewPOS: false,
  canViewCustomers: false,
  canViewPractitioners: false,
  canViewServices: false,
  canViewInventory: false,
  canViewReports: false,
  canViewSettings: false,
  canViewStaff: false,
  canViewSubscription: false,
  canViewBusinessProfile: false,
};

export function useStaffAccess() {
  const { user, session, business } = useAuth();
  const [staffBusiness, setStaffBusiness] = useState<StaffBusiness | null>(null);
  const [accessType, setAccessType] = useState<AccessType>("none");
  const [permissions, setPermissions] = useState<DashboardPermissions>(NO_PERMISSIONS);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const checkAccess = useCallback(async () => {
    if (!user || !session) {
      console.log("[useStaffAccess] No user or session");
      setLoading(false);
      setAccessType("none");
      setPermissions(NO_PERMISSIONS);
      return;
    }

    try {
      console.log("[useStaffAccess] Checking access for user:", user.id);

      // Call the edge function for backend authorization
      const { data, error: fnError } = await supabase.functions.invoke("verify-dashboard-access", {
        method: "POST",
      });

      if (fnError) {
        console.error("[useStaffAccess] Edge function error:", fnError);
        // Fallback to database function
        await fallbackToDatabase();
        return;
      }

      console.log("[useStaffAccess] Edge function response:", data);

      if (data?.hasAccess && data?.businessId) {
        setAccessType(data.accessType as AccessType);
        setStaffBusiness({
          id: data.businessId,
          business_name: data.businessName,
          subscription_plan: data.subscriptionPlan,
        });
        setPermissions(data.permissions);
        setError(null);
      } else {
        setAccessType("none");
        setStaffBusiness(null);
        setPermissions(NO_PERMISSIONS);
        if (data?.error) {
          console.log("[useStaffAccess] No access:", data.error);
        }
      }
    } catch (err) {
      console.error("[useStaffAccess] Error:", err);
      // Fallback to database function
      await fallbackToDatabase();
    } finally {
      setLoading(false);
    }
  }, [user, session]);

  const fallbackToDatabase = async () => {
    if (!user) return;

    try {
      console.log("[useStaffAccess] Falling back to database function");

      // Call the database function directly
      const { data, error: dbError } = await supabase
        .rpc("get_user_accessible_business", { p_user_id: user.id });

      if (dbError) {
        console.error("[useStaffAccess] Database function error:", dbError);
        setError(dbError.message);
        return;
      }

      const record = data?.[0];
      if (record && record.access_type !== "none" && record.business_id) {
        setAccessType(record.access_type as AccessType);
        setStaffBusiness({
          id: record.business_id,
          business_name: record.business_name,
          subscription_plan: record.subscription_plan,
        });
        setPermissions(calculatePermissions(record.access_type));
      } else {
        setAccessType("none");
        setStaffBusiness(null);
        setPermissions(NO_PERMISSIONS);
      }
    } catch (err) {
      console.error("[useStaffAccess] Fallback error:", err);
      setError(err instanceof Error ? err.message : "Unknown error");
    }
  };

  useEffect(() => {
    checkAccess();
  }, [checkAccess]);

  // Returns the business ID that the user has access to (either as owner or staff)
  const getAccessibleBusinessId = (): string | null => {
    return staffBusiness?.id || business?.id || null;
  };

  // Derived state
  const isOwner = accessType === "owner";
  const isManager = accessType === "manager";
  const isStaff = accessType === "staff";
  const hasAccess = accessType !== "none";

  return {
    staffBusiness,
    staffRole: isStaff ? "staff" : isManager ? "manager" : null,
    accessType,
    isStaff,
    isOwner,
    isManager,
    loading,
    error,
    permissions,
    getAccessibleBusinessId,
    hasAccess,
    refetch: checkAccess,
  };
}

function calculatePermissions(accessType: string): DashboardPermissions {
  switch (accessType) {
    case "owner":
      return {
        canViewOverview: true,
        canViewBookings: true,
        canViewPOS: true,
        canViewCustomers: true,
        canViewPractitioners: true,
        canViewServices: true,
        canViewInventory: true,
        canViewReports: true,
        canViewSettings: true,
        canViewStaff: true,
        canViewSubscription: true,
        canViewBusinessProfile: true,
      };
    case "manager":
      return {
        canViewOverview: true,
        canViewBookings: true,
        canViewPOS: true,
        canViewCustomers: true,
        canViewPractitioners: true,
        canViewServices: true,
        canViewInventory: true,
        canViewReports: true,
        canViewSettings: true,
        canViewStaff: false,
        canViewSubscription: false,
        canViewBusinessProfile: false,
      };
    case "staff":
      return {
        canViewOverview: true,
        canViewBookings: true,
        canViewPOS: true,
        canViewCustomers: true,
        canViewPractitioners: false,
        canViewServices: false,
        canViewInventory: false,
        canViewReports: false,
        canViewSettings: true,
        canViewStaff: false,
        canViewSubscription: false,
        canViewBusinessProfile: false,
      };
    default:
      return NO_PERMISSIONS;
  }
}
